// src/pages/dashboards/admin/AdminPartners.jsx
// Referral partners: the code they hand to their practitioner network, plus
// what commission is owed for a period.
//
// Two separate switches on a partner:
//   Active   - the code works for new practitioner signups
//   Terminated - commission stops from that date (also closes the code)

import React, { useEffect, useState } from "react";
import { listPartners, createPartner, updatePartner, getCommission } from "../../../api/admin";
import { Card, Input, Button, Badge, Loading, EmptyState, Modal, Field } from "../../../components/ui";
import { Plus, ChevronDown } from "../../../components/icons";
import { money, date, dateTime } from "../../../lib/format";
import { useToast } from "../../../context/ToastContext";

// first of the current month, as yyyy-mm-dd
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const pct = (r) => `${Math.round(Number(r || 0) * 1000) / 10}%`;

function CreatePartnerModal({ onClose, onCreated }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ name: "", code: "", commission_rate: "20" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createPartner({
        name: f.name.trim(),
        code: f.code.trim(),
        commission_rate: parseFloat(f.commission_rate) / 100,
      });
      toast.success("Partner created");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="New referral partner">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Partner name">
          <Input value={f.name} onChange={set("name")} required placeholder="Finsolve" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Join code" hint="practitioners enter this at signup">
            <Input
              value={f.code}
              onChange={(e) => setF((s) => ({ ...s, code: e.target.value.toUpperCase().replace(/\s/g, "") }))}
              required
              placeholder="FINSOLVE"
              className="font-mono"
            />
          </Field>
          <Field label="Commission" hint="percent of list price">
            <Input type="number" step="0.5" min="0" max="100" value={f.commission_rate} onChange={set("commission_rate")} required />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Creating..." : "Create partner"}</Button>
        </div>
      </form>
    </Modal>
  );
}

function CommissionRow({ p }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-tb-border/60 dark:border-tb-d-border/60">
        <td className="px-4 py-3 font-medium">{p.partner_name}</td>
        <td className="px-4 py-3">{p.clients}</td>
        <td className="px-4 py-3">{p.payments.length}</td>
        <td className="px-4 py-3">{money(p.revenue)}</td>
        <td className="px-4 py-3 text-tb-muted">{pct(p.commission_rate)}</td>
        <td className="px-4 py-3 font-semibold">{money(p.commission_due)}</td>
               <td className="px-4 py-3 text-right">
          <Button variant="ghost" onClick={() => setOpen((v) => !v)}>
            <ChevronDown size={16} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
          </Button>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-tb-border/60 dark:border-tb-d-border/60">
          <td colSpan={7} className="px-4 py-3 bg-tb-bg/60 dark:bg-tb-d-inset/40">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-tb-muted">
                  <th className="py-1.5 font-medium">Client</th>
                  <th className="py-1.5 font-medium">Practitioner</th>
                  <th className="py-1.5 font-medium">Tier</th>
                  <th className="py-1.5 font-medium">Source</th>
                  <th className="py-1.5 font-medium">Paid</th>
                  <th className="py-1.5 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {p.payments.map((x) => (
                  <tr key={x.payment_id}>
                    <td className="py-1.5">
                      {x.client_name}
                      <span className="text-tb-muted"> · {x.client_email}</span>
                    </td>
                    <td className="py-1.5">{x.practitioner_name}</td>
                    <td className="py-1.5">{x.tier}</td>
                    <td className="py-1.5">{x.source}</td>
                    <td className="py-1.5 text-tb-muted">{dateTime(x.paid_at)}</td>
                    <td className="py-1.5 text-right">{money(x.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminPartners() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);

  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const load = () =>
    listPartners().then(setRows).catch((e) => { toast.error(e.message); setRows([]); });

  const loadReport = () => {
    setLoadingReport(true);
    getCommission({ from, to })
      .then(setReport)
      .catch((e) => { toast.error(e.message); setReport(null); })
      .finally(() => setLoadingReport(false));
  };

  useEffect(() => { load(); loadReport(); /* eslint-disable-next-line */ }, []);

  const toggleActive = async (p) => {
    try {
      await updatePartner(p.id, { is_active: !p.is_active });
      toast.success(p.is_active ? "Code closed to new signups" : "Code reopened");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleTerminated = async (p) => {
    const ending = !p.terminated_at;
    if (ending && !window.confirm(`End the ${p.name} agreement? Commission stops from today.`)) return;
    try {
      await updatePartner(p.id, { terminated: ending });
      toast.success(ending ? "Agreement ended" : "Agreement reinstated");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const changeRate = async (p) => {
    const input = window.prompt(`Commission rate for ${p.name} (percent)`, String(Math.round(p.commission_rate * 1000) / 10));
    if (input === null) return;
    const v = parseFloat(input);
    if (isNaN(v) || v < 0 || v > 100) { toast.error("Enter a percentage between 0 and 100."); return; }
    try {
      await updatePartner(p.id, { commission_rate: v / 100 });
      toast.success("Rate updated");
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold">Referral partners</h1>
        <Button onClick={() => setCreating(true)}><Plus size={16} /> New partner</Button>
      </div>

      {/* partners */}
      <Card className="mb-8">
        {rows === null ? (
          <Loading label="Loading partners" />
        ) : rows.length === 0 ? (
          <EmptyState title="No partners yet" hint="Create one, then give them the join code for their practitioners." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                  <th className="px-4 py-3 font-medium">Partner</th>
                  <th className="px-4 py-3 font-medium">Code</th>
                  <th className="px-4 py-3 font-medium">Rate</th>
                  <th className="px-4 py-3 font-medium">Practitioners</th>
                  <th className="px-4 py-3 font-medium">Clients</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-b border-tb-border/60 dark:border-tb-d-border/60 last:border-0">
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3 font-mono">{p.code}</td>
                    <td className="px-4 py-3">{pct(p.commission_rate)}</td>
                    <td className="px-4 py-3">{p.practitioners}</td>
                    <td className="px-4 py-3">{p.clients}</td>
                    <td className="px-4 py-3">
                      {p.terminated_at ? (
                        <Badge tone="red">Ended {date(p.terminated_at)}</Badge>
                      ) : p.is_active ? (
                        <Badge tone="green">Active</Badge>
                      ) : (
                        <Badge tone="gray">Code closed</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" onClick={() => changeRate(p)}>Rate</Button>
                      {!p.terminated_at && (
                        <Button variant="ghost" onClick={() => toggleActive(p)}>
                          {p.is_active ? "Close code" : "Reopen"}
                        </Button>
                      )}
                      <Button variant="ghost" onClick={() => toggleTerminated(p)}>
                        {p.terminated_at ? "Reinstate" : "End"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* commission */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <h2 className="text-lg font-display font-semibold mr-auto">Commission owed</h2>
        <Field label="From"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Button onClick={loadReport} disabled={loadingReport}>{loadingReport ? "Loading..." : "Run"}</Button>
      </div>

      <Card>
        {loadingReport ? (
          <Loading label="Calculating" />
        ) : !report || report.partners.length === 0 ? (
          <EmptyState
            title="Nothing owed for this period"
            hint="Commission is counted on payments made by clients of partner-attributed practitioners."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                  <th className="px-4 py-3 font-medium">Partner</th>
                  <th className="px-4 py-3 font-medium">Clients</th>
                  <th className="px-4 py-3 font-medium">Payments</th>
                  <th className="px-4 py-3 font-medium">Revenue</th>
                  <th className="px-4 py-3 font-medium">Rate</th>
                  <th className="px-4 py-3 font-medium">Owed</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {report.partners.map((p) => <CommissionRow key={p.partner_id} p={p} />)}
                <tr className="bg-tb-bg/60 dark:bg-tb-d-inset/40">
                  <td className="px-4 py-3 font-semibold" colSpan={3}>Total</td>
                  <td className="px-4 py-3 font-semibold">{money(report.total_revenue)}</td>
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3 font-semibold">{money(report.total_commission)}</td>
                  <td className="px-4 py-3" />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="mt-4 text-xs text-tb-muted leading-relaxed max-w-2xl">
        Commission follows the client's current practitioner. If a client moves to a
        practitioner under a different partner, past payments move with them — so
        record what you pay rather than treating this report as a permanent record.
      </p>

      {creating && <CreatePartnerModal onClose={() => setCreating(false)} onCreated={load} />}
    </div>
  );
}